import React from 'react';

import { type Money, formatMoney } from '@betterment/core';
import { Box, Link, Separator, Text, type TextProps } from '@betterment/samba-react';

import {
  type Security,
  type SecurityPosition,
  type SecurityPositionBySecurity,
  isSecurityPositionBySecurity,
} from './data';
import { detailRowCss } from './SecurityGroupPositionSheet';
import { type SellButtonComponent } from './sellButtonHelpers';

import { formatNumberWithDecimals } from '../../utils/NumbersHelper';

type SecurityPositionItemProps = {
  securityPosition: SecurityPosition | SecurityPositionBySecurity;
  sellButton?: React.ReactElement<React.ComponentProps<SellButtonComponent>>;
};

export function SecurityPositionItem({ securityPosition, sellButton }: SecurityPositionItemProps) {
  if (isSecurityPositionBySecurity(securityPosition)) {
    return (
      <SecurityPositionItemWithMultipleSubAccounts
        securityPositionBySecurity={securityPosition}
        sellButton={sellButton}
      />
    );
  }

  return <SecurityPositionItemWithSingleSubAccount securityPosition={securityPosition} sellButton={sellButton} />;
}

type SecurityPositionItemWithSingleSubAccountProps = {
  securityPosition: SecurityPosition;
  sellButton?: React.ReactElement<React.ComponentProps<SellButtonComponent>>;
};

function SecurityPositionItemWithSingleSubAccount({
  securityPosition,
  sellButton,
}: SecurityPositionItemWithSingleSubAccountProps) {
  const { security } = securityPosition;
  const expenseRatio = security.expenseRatio?.value;

  return (
    <SecurityPositionItem.Layout security={security} sellButton={sellButton}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
        <SecurityPositionItem.Balance amount={securityPosition.amount} />
        <SecurityPositionItem.Shares shares={securityPosition.shares} />
        <SecurityPositionItem.ExpenseRatio expenseRatio={expenseRatio} label="Fund fee per year" />
      </Box>
    </SecurityPositionItem.Layout>
  );
}

type SecurityPositionItemWithMultipleSubAccountsProps = {
  securityPositionBySecurity: SecurityPositionBySecurity;
  sellButton?: React.ReactElement<React.ComponentProps<SellButtonComponent>>;
};

function SecurityPositionItemWithMultipleSubAccounts({
  securityPositionBySecurity,
  sellButton,
}: SecurityPositionItemWithMultipleSubAccountsProps) {
  const security = securityPositionBySecurity.securityPositionsBySubAccount[0]?.securityPositions[0]?.security;
  if (!security) return null;
  const expenseRatio = security.expenseRatio?.value;

  return (
    <SecurityPositionItem.Layout security={security} sellButton={sellButton}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
        <SecurityPositionItem.CurrentWeight currentWeight={securityPositionBySecurity.currentWeight} />
        <SecurityPositionItem.ExpenseRatio expenseRatio={expenseRatio} label="Fund fee" suffix="/yr" />
      </Box>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.100' }}>
        {securityPositionBySecurity.securityPositionsBySubAccount.map(subAccountPosition => (
          <Box
            key={subAccountPosition.subAccount.id}
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'space.50',
              py: 'space.100',
            }}
          >
            <Text variant="text50">{subAccountPosition.subAccount.name}</Text>
            <SecurityPositionItem.Balance amount={subAccountPosition.amount} color="text.weak" />
            <SecurityPositionItem.Shares shares={subAccountPosition.shares} color="text.weak" />
          </Box>
        ))}
      </Box>
    </SecurityPositionItem.Layout>
  );
}

const Layout: React.FC<{
  security: Security;
  sellButton?: React.ReactElement<React.ComponentProps<SellButtonComponent>>;
  children: React.ReactNode;
}> = ({ security, sellButton, children }) => (
  <>
    <Separator />
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
      <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {'name' in security && <Text>{security.name}</Text>}
          {'url' in security ? (
            <Link href={security.url} target="_blank" rel="noopener noreferrer" css={{ fontSize: 'fontSize.50' }}>
              {security.symbol}
            </Link>
          ) : (
            <Text>{security.symbol}</Text>
          )}
        </Box>
        {sellButton}
      </Box>
      {children}
    </Box>
  </>
);

const CurrentWeight: React.FC<{
  currentWeight: number;
  color?: TextProps['color'];
}> = ({ currentWeight, color }) => {
  return (
    <Box css={detailRowCss}>
      <Text variant="text50" color={color}>
        Current weight
      </Text>
      <Text variant="text50" color={color}>
        {formatNumberWithDecimals(currentWeight, 1)}%
      </Text>
    </Box>
  );
};

const Balance: React.FC<{
  amount: Money;
  color?: TextProps['color'];
}> = ({ amount, color }) => {
  return (
    <Box css={detailRowCss}>
      <Text variant="text50" color={color}>
        Balance
      </Text>
      <Text variant="text50" color={color}>
        {formatMoney(amount)}
      </Text>
    </Box>
  );
};

const Shares: React.FC<{
  shares: number;
  color?: TextProps['color'];
}> = ({ shares, color }) => {
  return (
    <Box css={detailRowCss}>
      <Text variant="text50" color={color}>
        Shares
      </Text>
      <Text variant="text50" color={color}>
        {formatNumberWithDecimals(shares)} shares
      </Text>
    </Box>
  );
};

const ExpenseRatio: React.FC<{
  expenseRatio?: number;
  label: string;
  suffix?: string;
  color?: TextProps['color'];
}> = ({ expenseRatio, label, suffix = '', color }) => {
  if (!expenseRatio) {
    return null;
  }

  return (
    <Box css={detailRowCss}>
      <Text variant="text50" color={color}>
        {label}
      </Text>
      <Text variant="text50" color={color}>
        {formatNumberWithDecimals(expenseRatio)}%{suffix}
      </Text>
    </Box>
  );
};

SecurityPositionItem.Balance = Balance;
SecurityPositionItem.CurrentWeight = CurrentWeight;
SecurityPositionItem.ExpenseRatio = ExpenseRatio;
SecurityPositionItem.Layout = Layout;
SecurityPositionItem.Shares = Shares;
